import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiEye,
  mdiLoading,
  mdiTrashCan,
} from "@mdi/js";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
} from "@mui/material";
import { green, red } from "@mui/material/colors";
import { createFileRoute, Navigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CreatePurchaseOrderByServiceQuotation } from "../../../../../@types/services/CreatePurchaseOrderByServiceQuotation";
import type { CreatePurchaseOrderByServiceQuotationExecution } from "../../../../../@types/services/CreatePurchaseOrderByServiceQuotationExecution";
import type { MaintenanceWindow } from "../../../../../@types/services/MaintenanceWindow";
import type { ServiceQuotation } from "../../../../../@types/services/ServiceQuotation";
import {
  removeCreatePurchaseOrderByServiceQuotation,
  showServiceQuotation,
} from "../../../../../__generated__/orval/endpoints/service-quotations/service-quotations";
import type { EntitiesServiceQuotationDetailOutput } from "../../../../../__generated__/orval/models";
import { CommonErrorHelp } from "../../../../../components/molecules/CommonErrorHelp";
import { ApiErrorsTable } from "../../../../../components/organisms/ApiErrorsTable";
import { getCreatePurchaseOrderByServiceQuotation } from "../../../../../services/createPurchaseOrderByServiceQuotations";
import { fetchMaintenanceWindows } from "../../../../../services/maintenanceWindows";
import { i18nextParseToDate } from "../../../../../utils/i18nextParseTo";

type ExecutionRowProps = {
  execution: CreatePurchaseOrderByServiceQuotationExecution;
};

const toLegacyServiceQuotation = (
  resource: EntitiesServiceQuotationDetailOutput,
): ServiceQuotation => {
  return resource as unknown as ServiceQuotation;
};

const ExecutionRow: React.FC<ExecutionRowProps> = ({ execution }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          borderLeftColor: execution.success ? green["500"] : red["500"],
          borderLeftStyle: "solid",
        }}
      >
        <TableCell>
          {Object.keys(execution.api_errors).length !== 0 && (
            <IconButton
              aria-label="expand row"
              onClick={() => setOpen(!open)}
              size="small"
            >
              {open ? (
                <Icon icon={mdiChevronUp} />
              ) : (
                <Icon icon={mdiChevronDown} />
              )}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <span>
            {t("intlDateTime", i18nextParseToDate(execution.created_at))}
          </span>
        </TableCell>

        <TableCell>
          <span>
            {t("intlDateTime", i18nextParseToDate(execution.updated_at))}
          </span>
        </TableCell>

        <TableCell align="center">
          {execution.completed ? (
            <span>{t("yes")}</span>
          ) : (
            <span>{t("no")}</span>
          )}
        </TableCell>

        <TableCell align="center">
          {execution.success ? <span>{t("yes")}</span> : <span>{t("no")}</span>}
        </TableCell>

        <TableCell align="center">
          {Object.keys(execution.api_errors).length !== 0 ? (
            <span>{t("yes")}</span>
          ) : (
            <span>{t("no")}</span>
          )}
        </TableCell>
      </TableRow>
      {Object.keys(execution.api_errors).length !== 0 && (
        <TableRow>
          <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <ApiErrorsTable apiErrors={execution.api_errors} />
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

type ServiceQuotationRowProps = {
  serviceQuotationId: number;
  createPurchaseOrderByServiceQuotation: CreatePurchaseOrderByServiceQuotation;
  lastExecution?: CreatePurchaseOrderByServiceQuotationExecution;
  tableRefreshCallback: React.Dispatch<React.SetStateAction<boolean>>;
};

const ServiceQuotationRow: React.FC<ServiceQuotationRowProps> = ({
  serviceQuotationId,
  createPurchaseOrderByServiceQuotation,
  lastExecution,
  tableRefreshCallback,
}) => {
  const { t } = useTranslation();

  const [serviceQuotation, setServiceQuotation] = useState<ServiceQuotation>();
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);

  const columnQuantity = useMemo(() => 7, []);

  const handleFetchServiceQuotation = useCallback((id: number): void => {
    setLoading(() => true);
    showServiceQuotation(id)
      .then((data) => {
        setServiceQuotation(() => toLegacyServiceQuotation(data));
      })
      .finally(() => {
        setLoading(() => false);
      });
  }, []);

  const handleRemoveServiceQuotation = useCallback(
    (id: number): void => {
      setLoadingRemove(() => true);
      removeCreatePurchaseOrderByServiceQuotation(id)
        .then(() => {
          tableRefreshCallback(() => true);
        })
        .finally(() => {
          setLoadingRemove(() => false);
          setShowConfirm(() => false);
        });
    },
    [tableRefreshCallback],
  );

  useEffect(() => {
    handleFetchServiceQuotation(serviceQuotationId);
  }, [serviceQuotationId, handleFetchServiceQuotation]);

  if (loading) {
    return (
      <TableRow>
        {Array(columnQuantity).map(() => (
          <TableCell key={`skeleton-cell-${crypto.randomUUID()}`}>
            <Skeleton />
          </TableCell>
        ))}
      </TableRow>
    );
  }

  if (!serviceQuotation) {
    return (
      <TableRow>
        <TableCell colSpan={columnQuantity}>Fail to load</TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell>{serviceQuotation.identifier}</TableCell>
      <TableCell>
        <span>
          {t(
            "intlDate",
            i18nextParseToDate(serviceQuotation.pr_item.requisition_date),
          )}
        </span>
      </TableCell>
      <TableCell>{serviceQuotation.pr_item.material?.matnumber}</TableCell>
      <TableCell>{serviceQuotation.vendor_winner?.fullvendorname}</TableCell>
      <TableCell>
        {serviceQuotation.purchase_order_item?.purchase_order.po_number}
      </TableCell>
      <TableCell>
        {serviceQuotation.purchase_order_item?.po_item_number}
      </TableCell>

      <TableCell align="right">
        {!serviceQuotation.purchase_order_item &&
          (serviceQuotation.create_purchase_order_item_jid ? (
            <Tooltip
              title={t(
                "serviceQuotation.info.purchase_order_item_relation_in_progress",
              )}
            >
              <Icon icon={mdiLoading} spin />
            </Tooltip>
          ) : (
            !createPurchaseOrderByServiceQuotation.completed &&
            (showConfirm ? (
              <Tooltip title={t("confirm")}>
                <Button
                  color="error"
                  loading={loadingRemove}
                  loadingPosition="center"
                  onClick={() =>
                    handleRemoveServiceQuotation(serviceQuotation.id)
                  }
                >
                  {t("confirm")}
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title={t("remove")}>
                <IconButton
                  disabled={lastExecution && !lastExecution.completed}
                  onClick={() => setShowConfirm(() => true)}
                >
                  <Icon icon={mdiTrashCan} />
                </IconButton>
              </Tooltip>
            ))
          ))}
        <Tooltip title={t("view")}>
          <IconButton
            component={Link}
            // to={`/vbuyer/quotations/${quotation.id}`}
            href={`/vbuyer/service_quotations/${serviceQuotation.id}`}
            target="_blank"
          >
            <Icon icon={mdiEye} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const ShowCreatePurchaseOrderByCreatePurchaseOrderByServiceQuotation: React.FC =
  () => {
    const { createPurchaseOrderByServiceQuotationId } = Route.useParams();

    const { t } = useTranslation();

    const [maintenanceWindows, setMaintenanceWindows] = useState<
      MaintenanceWindow[]
    >([]);
    const [
      createPurchaseOrderByServiceQuotation,
      setCreatePurchaseOrderByServiceQuotation,
    ] = useState<CreatePurchaseOrderByServiceQuotation>();
    const [notFound, setNotFound] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tabOpen, setTabOpen] = useState("service_quotations");
    const [refresh, setRefresh] = useState(true);
    const [loadingNewExecution, setLoadingNewExecution] = useState(false);
    const [lastExecution, setLastExecution] = useState<
      undefined | CreatePurchaseOrderByServiceQuotationExecution
    >();

    const didInit = useRef(false);

    const hasMaintenanceWindows = maintenanceWindows.length > 0;

    const handleFetchCreatePurchaseOrderByServiceQuotation = useCallback(
      async (id: string) => {
        setLoading(() => true);
        await getCreatePurchaseOrderByServiceQuotation(id)
          .then((resource) => {
            const temp = resource;
            temp.create_purchase_order_by_service_quotation_executions =
              resource.create_purchase_order_by_service_quotation_executions.sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime(),
              );
            setCreatePurchaseOrderByServiceQuotation(() => resource);
          })
          .catch(() => {
            setNotFound(() => true);
          })
          .finally(() => {
            setRefresh(() => false);
            setLoading(() => false);
          });
      },
      [],
    );

    const handleTabChange = (
      _event: React.SyntheticEvent,
      newValue: string,
    ): void => {
      setTabOpen(() => newValue);
    };

    const handleFetchMaintenanceWindows = useCallback(async () => {
      const response = await fetchMaintenanceWindows({
        filters: { active: true, system: "sap_p03" },
      });
      setMaintenanceWindows(
        response.results.filter(
          (maintenanceWindow) =>
            maintenanceWindow.blocked_actions.length === 0 ||
            maintenanceWindow.blocked_actions.includes(
              "issue_material_purchase_order",
            ),
        ),
      );
    }, []);

    useEffect(() => {
      if (!didInit.current) {
        didInit.current = true;
        handleFetchMaintenanceWindows();
      }
    }, [handleFetchMaintenanceWindows]);

    useEffect(() => {
      if (!refresh) {
        return;
      }

      if (!createPurchaseOrderByServiceQuotationId) {
        setNotFound(() => true);
        return;
      }

      handleFetchCreatePurchaseOrderByServiceQuotation(
        createPurchaseOrderByServiceQuotationId,
      );
    }, [
      handleFetchCreatePurchaseOrderByServiceQuotation,
      createPurchaseOrderByServiceQuotationId,
      refresh,
    ]);

    useEffect(() => {
      if (
        !createPurchaseOrderByServiceQuotation ||
        createPurchaseOrderByServiceQuotation
          .create_purchase_order_by_service_quotation_executions.length <= 0
      ) {
        return;
      }

      setLastExecution(
        () =>
          createPurchaseOrderByServiceQuotation
            .create_purchase_order_by_service_quotation_executions[0],
      );
    }, [createPurchaseOrderByServiceQuotation]);

    if (notFound || !createPurchaseOrderByServiceQuotationId) {
      return <Navigate to="/not_found" />;
    }

    return (
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          height: "100%",
          justifyContent: "center",
          px: 2,
          width: "100%",
        }}
      >
        <h1>{t("createPurchaseOrderByServiceQuotation.title")}</h1>

        {hasMaintenanceWindows &&
          maintenanceWindows.map((maintenanceWindow) => (
            <Alert key={maintenanceWindow.id} severity="warning" sx={{ mb: 2 }}>
              {maintenanceWindow.message}
            </Alert>
          ))}

        {createPurchaseOrderByServiceQuotation &&
          !createPurchaseOrderByServiceQuotation.completed &&
          (loadingNewExecution ? (
            <Button color="info" variant="outlined">
              <CircularProgress
                color="success"
                size={16}
                sx={{ marginRight: "5px" }}
              />
              <span>{t("createPurchaseOrderByServiceQuotation.runAgain")}</span>
            </Button>
          ) : (
            <Button
              disabled={
                (lastExecution &&
                  (!lastExecution.completed ||
                    !createPurchaseOrderByServiceQuotation.can_retry)) ||
                hasMaintenanceWindows
              }
              href={`/vbuyer/api/v2/create_purchase_order_by_service_quotations/${createPurchaseOrderByServiceQuotation.id}/generate_purchase_order`}
              onClick={() => setLoadingNewExecution(() => true)}
              type="button"
              variant="outlined"
            >
              {t("createPurchaseOrderByServiceQuotation.runAgain")}
            </Button>
          ))}

        <TabContext value={tabOpen}>
          <Box
            component="main"
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              height: "100%",
              justifyContent: "center",
              px: 2,
              width: "100%",
            }}
          >
            <Tabs onChange={handleTabChange} value={tabOpen}>
              <Tab
                label={t(
                  "createPurchaseOrderByServiceQuotation.service_quotations",
                )}
                value="service_quotations"
              />
              <Tab
                label={t("createPurchaseOrderByServiceQuotation.executions")}
                value="executions"
              />
            </Tabs>
            <CommonErrorHelp errorType="create_purchase_order_service_quotation" />
          </Box>

          <TabPanel value="service_quotations">
            <Box>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left">
                        {t("serviceQuotation.identifier")}
                      </TableCell>
                      <TableCell align="left">
                        {t("serviceQuotation.requisitionDate")}
                      </TableCell>
                      <TableCell align="left">
                        {t("serviceQuotation.service")}
                      </TableCell>
                      <TableCell align="left">
                        {t("serviceQuotation.vendor")}
                      </TableCell>
                      <TableCell align="left">Purchase Order</TableCell>
                      <TableCell align="left">Purchase Order Item</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!!loading && (
                      <TableRow>
                        {Array(7).map(() => (
                          <TableCell
                            key={`skeleton-cell-${crypto.randomUUID()}`}
                          >
                            <Skeleton />
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                    {!loading &&
                      createPurchaseOrderByServiceQuotation &&
                      createPurchaseOrderByServiceQuotation.service_quotations
                        ?.length > 0 &&
                      createPurchaseOrderByServiceQuotation.service_quotations.map(
                        (serviceQuotation) => (
                          <ServiceQuotationRow
                            createPurchaseOrderByServiceQuotation={
                              createPurchaseOrderByServiceQuotation
                            }
                            key={serviceQuotation.id}
                            lastExecution={lastExecution}
                            serviceQuotationId={serviceQuotation.id}
                            tableRefreshCallback={setRefresh}
                          />
                        ),
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          <TabPanel value="executions">
            <Box>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell align="left">
                        {t(
                          "createPurchaseOrderByServiceQuotationExecution.createdAt",
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {t(
                          "createPurchaseOrderByServiceQuotationExecution.updatedAt",
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {t(
                          "createPurchaseOrderByServiceQuotationExecution.completed",
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {t(
                          "createPurchaseOrderByServiceQuotationExecution.success",
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {t(
                          "createPurchaseOrderByServiceQuotationExecution.hasErrors",
                        )}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!!loading && (
                      <TableRow>
                        {Array(6).map(() => (
                          <TableCell
                            key={`skeleton-cell-${crypto.randomUUID()}`}
                          >
                            <Skeleton />
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                    {!loading &&
                      createPurchaseOrderByServiceQuotation &&
                      createPurchaseOrderByServiceQuotation
                        .create_purchase_order_by_service_quotation_executions
                        ?.length > 0 &&
                      createPurchaseOrderByServiceQuotation.create_purchase_order_by_service_quotation_executions.map(
                        (execution) => (
                          <ExecutionRow
                            execution={execution}
                            key={execution.id}
                          />
                        ),
                      )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>
        </TabContext>
      </Box>
    );
  };

export const Route = createFileRoute(
  "/_private/vbuyer/create_purchase_order_by_service_quotations/$createPurchaseOrderByServiceQuotationId/",
)({
  component: ShowCreatePurchaseOrderByCreatePurchaseOrderByServiceQuotation,
});
